import { FileText, Link2, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FadeIn } from '@/components/ui/fade-in';

interface HowItWorksProps {
  onOpenAuth?: (mode: 'login' | 'register') => void;
}

export default function HowItWorks({ onOpenAuth }: HowItWorksProps) {
  const steps = [
    {
      number: 1,
      icon: FileText,
      title: 'Create Your Form',
      description: 'Sign up and generate a webhook URL in seconds. Copy it.',
      visual: 'Dashboard screenshot',
    },
    {
      number: 2,
      icon: Link2,
      title: 'Connect Telegram',
      description: 'Send a one-time token to our bot. Works with any chat, group, or channel.',
      visual: '/connect command',
    },
    {
      number: 3,
      icon: Bell,
      title: 'Get Instant Notifications',
      description: "Form submissions arrive as formatted messages. That's it!",
      visual: 'Telegram message',
    },
  ];

  return (
    <section id="how-it-works" className="py-16 md:py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Setup in 3 Simple Steps
          </h2>
          <p className="text-lg text-gray-600">
            No coding required. No server setup. Just copy, paste, connect.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
          {steps.map((step) => {
            const Icon = step.icon;
            return (
              <FadeIn key={step.number} delay={step.number * 0.15}>
                <div className="text-center">
                  {/* Icon */}
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 text-white rounded-full mb-6 text-2xl font-bold">
                    {step.number}
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {step.number}. {step.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 mb-6">{step.description}</p>

                  {/* Visual Placeholder */}
                  <div className="aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <Icon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-xs text-gray-500">{step.visual}</p>
                    </div>
                  </div>
                </div>
              </FadeIn>
            );
          })}
        </div>

        <div className="text-center">
          <Button size="lg" onClick={() => onOpenAuth?.('login')}>
            Start Your First Form
          </Button>
        </div>
      </div>
    </section>
  );
}
